import { RuntimeFormatInput } from "./runtimeFormat";
import { DateAccuracy } from "./util";

export type BasicFieldType =
  | "checkbox"
  | "effort"
  | "customTab"
  | "group"
  | "crossReference"
  | "string";
export type DateFieldType = "date" | "dateRange";
export type ExtendedFieldType = "list" | "select";
export type StoredFieldType =
  | DateFieldType
  | BasicFieldType
  | ExtendedFieldType;

export type BasicStoredField = {
  type: StoredFieldType;
  key: string;
  title: string;
  required: boolean;
  noAdvancedImport: boolean;
};
export type ListStoredField = BasicStoredField & {
  type: "list";
  sublist: {
    label: string;
    name: string;
  };
};
export type SelectStoredField = BasicStoredField & {
  type: "select";
  options: string[];
  allowOther: boolean;
  extraPI: boolean;
};
export type DateStoredField = BasicStoredField & {
  type: "date";
  isPrimaryDate?: boolean; // Is this the date at the header of the entry, or a date in the entry itself
  allowedAccuracy: DateAccuracy;
  requiredAccuracy: DateAccuracy;
};
export type DateRangeSide = "start" | "end";
export type DateRangeStoredField = BasicStoredField & {
  type: "dateRange";
  isPrimaryDate?: boolean;
  start: {
    allowedAccuracy: DateAccuracy;
    requiredAccuracy: DateAccuracy;
  };
  end: {
    // if undefined, end is not allowed
    optional: boolean;
    allowedAccuracy: DateAccuracy;
    requiredAccuracy: DateAccuracy; // none = end date optional
    allowOngoing: boolean;
  };
};
export type StoredField<T extends StoredFieldType = StoredFieldType> =
  T extends "list"
    ? ListStoredField
    : T extends "select"
      ? SelectStoredField
      : T extends "dateRange"
        ? DateRangeStoredField
        : T extends "date"
          ? DateStoredField
          : BasicStoredField;

export enum StoredSubtabFieldSource {
  Parent,
  Private
}
export interface StoredSubtab {
  key: string;
  title: string;
  // Subtabs have no knowledge of sibling subtab fields, they do not need to know
  // Private: fields belonging to this subtab and not the parent
  privateFields: StoredField<StoredFieldType>[];
  // Override: fields belonging to parent that are changed in this subtab
  overrideFields: (Partial<StoredField<StoredFieldType>> & { key: string })[];
  // Rejected: fields belonging to parent that are hidden in this subtab
  rejectFields: string[];

  // Field order. Each element will be `Parent` or `Private`. When arranging fields in an order,
  //  we step thru this array and if the element is `Parent` we pull in the next parent field, if it's
  //  `Private` we pull in the next private field. Override fields are applied to the parent fields regardless
  //  of order, and the reject fields of this subtab are COMPLETELY IGNORED for ordering purposed
  fieldOrdering: StoredSubtabFieldSource[];
}
export type StoredTabType = "obj" | "list" | "custom";
export type StoredTabOrder = "chron" | "reverseChron";
export const DEFAULT_TAB_SECTION = "Global";
export const STORED_TAB_SETUP_FNS = [
  "citationNameSetup" as const,
  "scholarshipSetup" as const
] as const;
// same as StoredTabSetup = "citationNameSetup" | "scholarshipSetup" ..., except we now have access to it in both a tuple form and a union form;
// this allows for a runtime-compatible check on the list of available setup functions.
export type StoredTabSetup = (typeof STORED_TAB_SETUP_FNS)[number];
export interface StoredTab {
  section: string;
  key: string;

  type: StoredTabType;
  title: string;

  setup?: StoredTabSetup;

  order?: StoredTabOrder;

  fields: StoredField<StoredFieldType>[];
  subtabs: StoredSubtab[];

  /*
   * conflicting?: number
   * undefined if no conflict.
   * number if conflict
   * the number is the order that the conflict will be resolved in when edits are made
   *
   * examples:
   *  tab X and Y conflict, X was added first so it is shown on the legacy side
   *  tab X's conflict field is undefined. tab Y's conflict field is 0.
   *  tab X is removed or changed sections => tab Y's conflict field is set to undefined. tab Y is now shown.
   *
   *  tab X, Y, and Z are conflicts, added in the order X -> Y -> Z.
   *  X is shown on legacy side because it was added first.
   *  tab X's conflict field is undefined. tab Y's conflict field is 0. tab Z's conflict field is 1
   *  tab X is removed or changed sections => tab Y's conflict field is set to undefined, tab Z's conflict field is decremented by 1.
   *  tab Y is now shown on the legacy size, tab Z is still conflicting and hidden.
   */
  conflicting?: number;
}
export type StoredOutputFormat = (
  item: {},
  i: string,
  cvDef: {},
  genOpts: {}
) => string | string;

export interface StoredOutput {
  field: string;
  filter?: (...args: any) => boolean; // TODO: More specific
  preface?: (...args: any) => boolean | boolean; // TODO: More specific
  map?: { [key: string]: string | Function };
  format?: {
    [key: string]: StoredOutputFormat[];
  };
  type?: string;
  sections?:
    | ((...args: any) => any) /* TODO: More specific */
    | (StoredOutputFormat & { title?: string })[];
  toggle?: (...args: any) => boolean; // TODO: More specific
}

export type StoredPreviewMacro =
  | {
      macro: "previewTextField";
      name: string;
      prefix: string;
      suffix: string;
    }
  | {
      macro: "otherExample";
      prop1: string;
      prop2: number;
    };
export type FormatAuthorID = `u${number}`;
export type OutputFont = "Times New Roman" | "Arial" | "Georgia";
export type OutputSourceType = "dynamic" | "static";
export type OutputSource = `${"twiz" | "legacy"}/${string}`;
export type OutputFile = `/templateFiles/${string}.docx`;
export type OutputSettings = {
  font: OutputFont;
  fontSize: number;
};
export interface StoredFormat {
  _id: string;
  createdAt: Date;
  authorId: FormatAuthorID;
  title: string;
  tabs: StoredTab[];

  outputSourceType: OutputSourceType;
  outputSource: OutputSource;
  outputFile?: OutputFile;
  output: Record<string, StoredOutput>;
  outputSettings?: OutputSettings;
  preview: StoredPreviewMacro[];
  // twiz does not know this field exists and will not read or write to it
  // only the Format Wrapper will read from this field
  // added manually for bandaids/edge cases
  nonParsable?: NonParsable[];
}

export type NoParseType = "tab" | "tabReference";
type NoParseBase = {
  _noParseType: NoParseType;
};
type PartiallyRequired<T, K extends keyof T> = Partial<T> & {
  [P in K]-?: T[P];
};
type NoParseTab = RuntimeFormatInput &
  NoParseBase & {
    _noParseType: "tab";
    _attachToPartialTab: PartiallyRequired<StoredTab, "key" | "section">;
  };
type NoParseTabReference = NoParseBase & {
  _noParseType: "tabReference";
  _referenceFormat: string;
  _referenceTabKey: string;
  _referenceTabSection: string | undefined;
  _attachToPartialTab: PartiallyRequired<StoredTab, "key" | "section">;
  _edits: object[];
};
export type NonParsable<T extends NoParseType = NoParseType> = T extends "tab"
  ? NoParseTab
  : T extends "tabReference"
    ? NoParseTabReference
    : never;
